/**
 * Error logging utility for debugging
 */

/**
 * Log error to console with context
 * @param {Error} error - The error object
 * @param {Object} context - Additional context information
 */
export function logError(error, context = {}) {
  const timestamp = new Date().toISOString();
  const errorLog = {
    timestamp,
    message: error.message,
    stack: error.stack,
    context,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Error logged:', errorLog);
  }

  // In production, you could send to an error tracking service
  // Example: Sentry, LogRocket, etc.
  if (process.env.NODE_ENV === 'production') {
    // sendToErrorTrackingService(errorLog);
  }

  return errorLog;
}

/**
 * Log API error with request details
 * @param {Error} error - The error object
 * @param {Object} requestConfig - Axios request config
 */
export function logApiError(error, requestConfig = {}) {
  const context = {
    type: 'API_ERROR',
    method: requestConfig.method,
    url: requestConfig.url,
    status: error.response?.status,
    statusText: error.response?.statusText,
    data: error.response?.data,
  };

  return logError(error, context);
}

/**
 * Log warning message
 * @param {string} message - Warning message
 * @param {Object} context - Additional context
 */
export function logWarning(message, context = {}) {
  const timestamp = new Date().toISOString();
  const warningLog = {
    timestamp,
    level: 'warning',
    message,
    context,
  };

  console.warn('Warning:', warningLog);
  return warningLog;
}

/**
 * Initialize global error logger
 */
export function initErrorLogger() {
  // Attach to window for ErrorBoundary
  window.logError = logError;

  // Global error handler
  window.addEventListener('error', (event) => {
    logError(event.error || new Error(event.message), {
      type: 'GLOBAL_ERROR',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  // Unhandled promise rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    logError(
      event.reason instanceof Error ? event.reason : new Error(event.reason),
      {
        type: 'UNHANDLED_REJECTION',
      }
    );
  });
}
